import { createFileRoute } from '@tanstack/react-router';
import { Sidebar } from '@/components/layout/sidebar';
import { useCommissionSummary, usePendingCommissionActions } from '@/features/admin/commission-queries';
import { formatMoney } from '@/lib/format';

export const Route = createFileRoute('/_auth/admin/commission')({ component: AdminCommissionPage });

function AdminCommissionPage(): JSX.Element {
  const { data: summary, isLoading: sLoading } = useCommissionSummary();
  const { data: pending, isLoading: pLoading } = usePendingCommissionActions();

  return (
    <div className="flex h-screen bg-bg text-text">
      <Sidebar />
      <main className="flex-1 overflow-auto">
        <div className="pl-14 pr-6 md:px-6 py-4 border-b border-border/[0.08]">
          <h1 className="font-display text-2xl">Commission</h1>
          <p className="text-text-dim text-sm mt-1">Per-agent earnings and pending actions.</p>
        </div>

        <section className="px-6 py-5">
          <h2 className="text-sm font-semibold mb-3">Per-agent summary</h2>
          {sLoading ? (
            <p className="text-text-mute text-sm">Loading…</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-text-mute text-xs uppercase tracking-wider border-b border-border/[0.08]">
                    <th className="py-3">Agent</th>
                    <th className="py-3 text-right">Closes</th>
                    <th className="py-3 text-right">Earned</th>
                    <th className="py-3 text-right">Pending</th>
                    <th className="py-3 text-right">Paid</th>
                    <th className="py-3 text-right">Per Close</th>
                  </tr>
                </thead>
                <tbody>
                  {(summary ?? []).length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-text-mute">No agents yet.</td>
                    </tr>
                  ) : (
                    summary?.map((row) => (
                      <tr key={row.agent_id ?? row.full_name ?? Math.random()} className="border-b border-border/[0.04]">
                        <td className="py-3 font-medium">{row.full_name ?? '—'}</td>
                        <td className="py-3 text-right font-mono">{row.qualifying_closes ?? 0}</td>
                        <td className="py-3 text-right font-mono">{formatMoney(row.total_earned ?? 0)}</td>
                        <td className="py-3 text-right font-mono text-warn">{formatMoney(row.total_pending ?? 0)}</td>
                        <td className="py-3 text-right font-mono text-success">{formatMoney(row.total_paid ?? 0)}</td>
                        <td className="py-3 text-right font-mono">{formatMoney(row.commission_per_close ?? 0)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="px-6 py-5">
          <h2 className="text-sm font-semibold mb-3">Pending actions</h2>
          {pLoading ? (
            <p className="text-text-mute text-sm">Loading…</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-text-mute text-xs uppercase tracking-wider border-b border-border/[0.08]">
                    <th className="py-3">Company</th>
                    <th className="py-3">Agent</th>
                    <th className="py-3">Status</th>
                    <th className="py-3">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {(pending ?? []).length === 0 ? (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-text-mute">No pending actions.</td>
                    </tr>
                  ) : (
                    pending?.map((row) => (
                      <tr key={row.opportunity_id ?? Math.random()} className="border-b border-border/[0.04]">
                        <td className="py-3">{row.company_name ?? '—'}</td>
                        <td className="py-3 text-text-dim">{row.agent_name ?? '—'}</td>
                        <td className="py-3">
                          <span className="text-xs px-2 py-0.5 rounded bg-surface-2 border border-border/[0.08]">
                            {row.status ?? '—'}
                          </span>
                        </td>
                        <td className="py-3 text-text-mute text-xs font-mono">
                          {row.updated_at ? new Date(row.updated_at).toLocaleDateString('en-GB') : '—'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
